import { outdent } from 'outdent';
import type {
  DevCenterComponent,
  SiteComponentWithPreview,
  SiteComponentWithSdk,
} from '../../../dev-center/components/dev-center-component.js';
import {
  getComponentPathWithHMR,
  getSiteComponentPathWithHMR,
  getSiteComponentSdkPathWithHMR,
} from '../hmr/plugin.js';

const getSiteComponentBaseTemplate = (applicationId: string, path: string) => {
  return outdent`
    import { enableClient } from '@wix/cli-app/internal/platform-sdk/site';

    const { injectGetAccessToken: injectAccessTokenGetter } = enableClient('${applicationId}');

    export { injectAccessTokenGetter };

    export { default } from '${path}';
    export * from '${path}';
  `;
};

export const getSiteComponentDevTemplate = (
  applicationId: string,
  component: DevCenterComponent<'SiteComponent'>
) =>
  getSiteComponentBaseTemplate(
    applicationId,
    getSiteComponentPathWithHMR(component.relativePath)
  );

export const getSiteComponentPreviewDevTemplate = (
  applicationId: string,
  component: SiteComponentWithPreview
) =>
  getSiteComponentBaseTemplate(
    applicationId,
    getSiteComponentPathWithHMR(component.relativePreviewPath)
  );

export const getSiteComponentBuildTemplate = (
  applicationId: string,
  component: DevCenterComponent<'SiteComponent'>
) => getSiteComponentBaseTemplate(applicationId, `/${component.relativePath}`);

export const getSiteComponentPreviewBuildTemplate = (
  applicationId: string,
  component: SiteComponentWithPreview
) =>
  getSiteComponentBaseTemplate(
    applicationId,
    `/${component.relativePreviewPath}`
  );

export const getSiteComponentSdkDevTemplate = (
  component: SiteComponentWithSdk
) => {
  return `export { default } from '${getSiteComponentSdkPathWithHMR(component.relativeSdkPath)}';`;
};

export const getSiteComponentSdkBuildTemplate = (
  component: SiteComponentWithSdk
) => {
  return `export { default } from '/${component.relativeSdkPath}';`;
};

const getSiteComponentPanelBaseTemplate = (path: string) =>
  outdent`
    import { enablePanelClient } from '@wix/cli-app/internal/platform-sdk/site';
    import Component from '${path}';

    export default (config) => {
      const clientConfig = (config.host && config.auth) ? {
        host: config.host,
        auth: config.auth
      } : { host: config };

      enablePanelClient(clientConfig);

      return Component;
    }
  `;

export const getSiteComponentPanelDevTemplate = (
  component: DevCenterComponent<'SiteComponentPanel'>
) =>
  getSiteComponentPanelBaseTemplate(
    getComponentPathWithHMR(component.relativePath)
  );

export const getSiteComponentPanelBuildTemplate = (
  component: DevCenterComponent<'SiteComponentPanel'>
) => getSiteComponentPanelBaseTemplate(`/${component.relativePath}`);
